import { api } from "../api";

export interface BookCondition {
  id: string;
  tenantId: string;
  code: string;
  label: string;
  description?: string | null;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBookConditionDto {
  code: string;
  label: string;
  description?: string;
  order?: number;
  isActive?: boolean;
}

export type UpdateBookConditionDto = Partial<CreateBookConditionDto>;

export const bookConditionsApi = {
  /**
   * Get all book conditions for the current tenant (authenticated)
   */
  getAll: async (includeInactive = false): Promise<BookCondition[]> => {
    const response = await api.get("/book-conditions", {
      params: { includeInactive },
    });
    return response.data;
  },

  /**
   * Get a single book condition by ID
   */
  getById: async (id: string): Promise<BookCondition> => {
    const response = await api.get(`/book-conditions/${id}`);
    return response.data;
  },

  /**
   * Create a new book condition
   */
  create: async (data: CreateBookConditionDto): Promise<BookCondition> => {
    const response = await api.post("/book-conditions", data);
    return response.data;
  },

  /**
   * Update an existing book condition
   */
  update: async (id: string, data: UpdateBookConditionDto): Promise<BookCondition> => {
    const response = await api.put(`/book-conditions/${id}`, data);
    return response.data;
  },

  /**
   * Delete a book condition
   * @param id - Book condition ID
   * @param hardDelete - If true, permanently delete. If false or undefined, soft delete (set inactive).
   */
  delete: async (id: string, hardDelete?: boolean): Promise<void> => {
    const params = hardDelete ? { hardDelete: "true" } : {};
    await api.delete(`/book-conditions/${id}`, { params });
  },

  /**
   * Reorder book conditions by updating their order values
   * @param orderedIds - Array of book condition IDs in the desired order
   */
  reorder: async (orderedIds: string[]): Promise<void> => {
    // Update each condition's order based on its position in the array
    const updatePromises = orderedIds.map((id, index) =>
      api.put(`/book-conditions/${id}`, { order: index })
    );
    await Promise.all(updatePromises);
  },
};
